import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFn } from '@tanstack/react-router';

import { getForecastSessionsQueryKey } from 'src/keys';
import {
  getForecastSessions,
  type SessionSummary,
} from 'src/services/api/getForecastSessions';

type RenameSessionAndNavigateParams = {
  navigate: NavigateFn;
  nextSessionName: string;
  queryClient: QueryClient;
  renameForecastSession: (values: {
    sessionId: string;
    name: string;
  }) => Promise<SessionSummary>;
  sessionToRename: SessionSummary;
};

function buildRenameSessionPayload(sessionId: string, nextSessionName: string) {
  return {
    sessionId,
    name: nextSessionName.trim(),
  };
}

export function getTargetSession(
  previousSessions: SessionSummary[],
  refreshedSessions: SessionSummary[],
): SessionSummary | undefined {
  const previousSessionIds = new Set(
    previousSessions.map((session) => session.id),
  );
  const sortedSessions = [...refreshedSessions].sort((left, right) => {
    return (
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
    );
  });

  return (
    (sortedSessions[0] &&
      sortedSessions.find((session) => !previousSessionIds.has(session.id))) ??
    sortedSessions[0]
  );
}

export async function navigateToConfigurationSession(
  navigate: NavigateFn,
  sessionId: string,
) {
  await navigate({
    to: '/configuration',
    search: { sessionId },
  });
}

export async function resolveNextSessionAfterLastAction(
  queryClient: QueryClient,
  previousSessions: SessionSummary[],
) {
  const refreshedSessions = await queryClient.fetchQuery({
    queryKey: getForecastSessionsQueryKey(),
    queryFn: async () => getForecastSessions(),
  });
  const targetSession = getTargetSession(previousSessions, refreshedSessions);

  return {
    isNewSession: Boolean(
      targetSession &&
        !previousSessions.some((session) => session.id === targetSession.id),
    ),
    targetSession,
  };
}

export async function renameSessionAndNavigate({
  navigate,
  nextSessionName,
  queryClient,
  renameForecastSession,
  sessionToRename,
}: RenameSessionAndNavigateParams) {
  const renamedSession = await renameForecastSession(
    buildRenameSessionPayload(sessionToRename.id, nextSessionName),
  );
  await queryClient.invalidateQueries({
    queryKey: getForecastSessionsQueryKey(),
  });
  await navigateToConfigurationSession(navigate, renamedSession.id);
}
